import { Autocomplete, Box, Divider, IconButton, TextField } from "@mui/material";
import LaunchIcon from "@mui/icons-material/Launch";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import axios from "axios";
import axiosInstance from "clients/api";
import usePermission from "components/complex/permission/hook";
import useStateComplex from "hooks/common/useStateComplex";
import PropTypes from "prop-types";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form-v7";
import { useTheme } from "@mui/material/styles";
import {
  getEntityDetailUrl,
  RELATED_ENTITY_PERMISSION_BY_KIND
} from "utils/helpers/entityDetailUrl";
import getStyles from "../style";
import useDeviceType from "../../../../hooks/common/useDeviceType";
import { v4 as uuidv4 } from "uuid";

const SelectWithAddSearch = ({
  createDialog: CreateDialog,
  control,
  setValue,
  setError,
  clearErrors,
  error,
  url,
  name,
  apiDataKey,
  label,
  selectCompany,
  moduleName,
  initialData,
  disabled,
  combineName,
  valueDefault,
  isContactWithCompany,
  getAddressDataCallback,
  customItem,
  recordId,
  isAddress,
  apiNameKey,
  addOriginalData,
  relatedEntityLink,
  onOpenRelatedEntity
}) => {
  const isWithAddNewFeature = !!CreateDialog;
  const hasRelatedEntityOpen = Boolean(relatedEntityLink || onOpenRelatedEntity);

  const { isMobile } = useDeviceType();
  const theme = useTheme();
  const styles = getStyles(theme, { isMobile });
  const addPermission = moduleName ? usePermission(moduleName, "Add") : false;
  const relatedPermissionModule = relatedEntityLink?.kind
    ? RELATED_ENTITY_PERMISSION_BY_KIND[relatedEntityLink.kind]
    : onOpenRelatedEntity
      ? "opportunity_permission"
      : undefined;
  const relatedEntityPermissions = usePermission(relatedPermissionModule);
  const initialOptions = customItem?.length ? [...customItem] : [];
  const [defaultValue, setDefaultValue] = useState(valueDefault);

  useEffect(() => {
    setDefaultValue(valueDefault);
  }, [valueDefault]);

  const [addNew, setAddNew] = useState(false);
  const [state, setState] = useStateComplex({
    isLoading: false,
    list: initialOptions
  });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const debounceRef = useRef(null);
  const typedTextRef = useRef("");

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownElement = document.getElementById("autocomplete");
      // Check if the click is inside the Autocomplete or its input field
      if (!event.target.contains(dropdownElement)) {
        setIsDropdownOpen(false); // Close the dropdown
      }
    };

    document.addEventListener("click", handleClickOutside);

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const axiosSourceRef = useRef(null);

  const setRecordValue = useCallback(
    (row) => {
      if (!row) {
        return;
      }
      const recordData = {
        code: row[apiDataKey || name],
        name: row.name ? row.name : row[apiNameKey]
      };
      setValue(name, recordData, { shouldValidate: false, shouldDirty: false });
    },
    [apiDataKey, apiNameKey, name, setValue]
  );

  const setTableData = (data) => {
    // console.log('clearing error');
    // clearErrors(name);
    const newData = data?.tableData?.map((c) => ({
      code: c[apiDataKey || name],
      name: combineName ? `${c.name} | ${c.email}` : c.name ? c.name : c[apiNameKey],
      ...(addOriginalData ? { original: c } : {}),
      ...(selectCompany &&
        c?.company?.company_id && {
          company: {
            code: c.company.company_id,
            name: c.company.name
          }
        })
    }));
    setState({
      list: customItem ? [...customItem, ...newData] : [...newData],
      isLoading: false
    });

    if (recordId && data?.tableData?.length) {
      const row =
        data.tableData.find((item) => String(item[apiDataKey || name]) === String(recordId)) ||
        data.tableData[0];
      setRecordValue(row);
    }
  };

  const setAddressData = (data) => {
    clearErrors(name);
    const newData = data?.map((address) => ({
      code: uuidv4(),
      name: `${address.addressLabel} ${address.city ? `, ${address.city}` : ""} ${address.stateCode ? `, ${address.stateCode}` : ""}`,
      country: address.countryCode,
      city: address.city,
      state: address.stateCode,
      postal_code: address.postalCode,
      [name]: {
        name: address.addressLabel,
        code: address.addressLabel
      }
    }));
    setState({
      list: customItem ? [...customItem, ...newData] : [...newData],
      isLoading: false
    });
  };

  const loadData = async (searchValue) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (axiosSourceRef.current) {
      axiosSourceRef.current.cancel("Operation canceled — user typed.");
      axiosSourceRef.current = null;
    }

    const trimmed = (searchValue || "").trim();
    if (isAddress && trimmed.length < 3) {
      setState({ isLoading: false });
      return;
    }

    const debounceMs = isAddress ? 500 : 300;

    debounceRef.current = setTimeout(async () => {
      const source = axios.CancelToken.source();
      axiosSourceRef.current = source;

      setState((prev) => ({
        ...prev,
        isLoading: true
      }));

      try {
        const params = {};
        if (isAddress) {
          params.address = encodeURIComponent(trimmed);
        } else {
          params.search_value = searchValue || initialData;
        }
        const { data } = await axiosInstance.get(url, {
          cancelToken: source.token,
          params
        });

        if (axiosSourceRef.current !== source) return;

        if (isAddress) {
          setAddressData(data);
        } else if (data.tableData) {
          clearErrors(name);
          setTableData(data);
        }
      } catch (err) {
        if (axios.isCancel(err)) return;

        !isAddress &&
          setError(name, {
            type: `${name}.failed`,
            message: `Failed to load ${label}(s)!`
          });

        setState({
          isLoading: false
        });
      }
    }, debounceMs);
  };

  // On item click
  const onSelect = useCallback(
    (props, data) => {
      if (isAddress) {
        getAddressDataCallback(data);
        const newData = data ? { name: data[name].name, code: data.code } : "";
        return props.onChange(newData);
      }
      if (isWithAddNewFeature && data?.index === 0) {
        // Add new item
        setAddNew(true);
      } else {
        // Select from items list
        const d = data
          ? {
              code: data.code,
              name: data.name,
              ...(selectCompany && data?.company ? { company: data.company } : {})
            }
          : "";

        if (selectCompany && d?.company) {
          selectCompany(d.company);
          delete d.company;
        }
        clearErrors(name);
        setValue(name, d, { shouldDirty: true, shouldValidate: true });
      }
    },
    [isWithAddNewFeature, selectCompany, isAddress, clearErrors, name, setValue]
  );

  // On new item create
  const onItemCreate = useCallback(
    (itemData) => {
      const createdItem = {
        code: itemData.code,
        name: itemData.name
      };
      setState({
        list: [itemData, ...state.list]
      });

      setDefaultValue(itemData);

      // Set current key in form
      setValue(name, createdItem, {
        shouldDirty: true,
        shouldValidate: true
      });

      // Set company if needed
      if (selectCompany && itemData?.company) {
        selectCompany(itemData.company);
      }
    },
    [selectCompany, state?.list]
  );

  useEffect(() => {
    if (initialData) {
      if (initialData?.tableData) {
        setTableData(initialData);
      }
    } else if (!isAddress) {
      setTimeout(() => loadData(recordId || ""), 0);
    }
  }, [initialData, recordId]);

  useEffect(() => {
    if (!valueDefault?.code && valueDefault?.code !== 0) {
      return;
    }

    setState({
      list: state.list.some((item) => item.code === valueDefault.code)
        ? state.list
        : [...state.list, valueDefault]
    });
  }, [valueDefault]);

  useEffect(
    () => () => {
      setDefaultValue(null);
    },
    []
  );

  return (
    <div id="autocomplete">
      <Controller
        control={control}
        name={name}
        {...(defaultValue != null ? { defaultValue } : {})}
        render={({ field }) => {
          const entityId = field.value?.code;
          const canOpenRelated =
            hasRelatedEntityOpen &&
            entityId != null &&
            entityId !== "" &&
            entityId !== "add-new" &&
            entityId !== "divider" &&
            (relatedEntityPermissions?.Edit || relatedEntityPermissions?.View);

          const handleOpenRelated = (event) => {
            event.stopPropagation();
            event.preventDefault();
            if (!entityId) {
              return;
            }
            if (onOpenRelatedEntity) {
              onOpenRelatedEntity(entityId);
              return;
            }
            if (relatedEntityLink?.kind) {
              const detailUrl = getEntityDetailUrl(
                relatedEntityLink.kind,
                entityId,
                relatedEntityPermissions
              );
              if (detailUrl) {
                window.open(detailUrl, "_blank", "noopener,noreferrer");
              }
            }
          };

          return (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Autocomplete
                  {...field}
                  value={field.value ?? null}
                  size="small"
                  sx={styles.autoCompleteRoot}
                  options={
                    isWithAddNewFeature && addPermission
                      ? [
                          {
                            index: 0,
                            code: "add-new",
                            name: `+ Add New ${label}`
                          },
                          {
                            index: 1,
                            code: "divider",
                            name: "divider",
                            disabled: true
                          },
                          ...state.list
                        ]
                      : state.list
                  }
                  loading={state.isLoading}
                  loadingText="Loading..."
                  popupIcon={isAddress && null}
                  noOptionsText={isAddress && "Start typing to search address…"}
                  // disableClearable
                  disabled={disabled}
                  filterOptions={(options) => options}
                  isOptionEqualToValue={(option, val) =>
                    option?.code != null && val?.code != null && option.code === val.code
                  }
                  getOptionLabel={(option) => option?.name || ""}
                  renderOption={({ key, ...optionProps }, option, { selected }) =>
                    option.code === "divider" ? (
                      <li key="divider">
                        <Divider />
                      </li>
                    ) : (
                      <li {...optionProps} key={option.code}>
                        {isContactWithCompany && option?.company?.name
                          ? `${option.name} | ${option.company.name}`
                          : option.name}
                      </li>
                    )
                  }
                  // renderOption={option => option.name}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={label}
                      variant="outlined"
                      size="small"
                      margin="normal"
                      sx={{
                        ...styles.field,
                        "& .MuiInputBase-input": styles.input
                      }}
                      error={Boolean(error)}
                      helperText={<span>{error || null}</span>}
                      FormHelperTextProps={{
                        sx: styles.errorText
                      }}
                      open={isDropdownOpen}
                      onOpen={() => setIsDropdownOpen(true)}
                      onClose={() => setIsDropdownOpen(false)}
                      inputProps={{
                        ...params.inputProps,
                        autoComplete: "new-password",
                        onClick: (e) => e.stopPropagation()
                      }}
                      form={{
                        autoComplete: "off"
                      }}
                      InputLabelProps={{
                        sx: {
                          ...styles.label,
                          ...styles.labelNotCapitalize,
                          "&.Mui-focused": styles.labelFocused,
                          "&.MuiInputLabel-shrink": styles.labelShrink
                        }
                      }}
                      onChange={(e) => {
                        typedTextRef.current = e.target.value;
                        loadData(e.target.value);
                      }}
                      onBlur={() => {
                        if (!isAddress) return;
                        if (field.value?.code) return;
                        const typed = (typedTextRef.current || "").trim();
                        if (typed && typed !== field.value?.name) {
                          setValue(name, { name: typed }, { shouldDirty: true });
                        }
                      }}
                      disabled={disabled}
                    />
                  )}
                  onChange={(_, data) => onSelect(field, data)}
                  // value="" // TODO -> fix for uncontrolled select
                />
              </Box>
              {canOpenRelated && (
                <IconButton
                  size="small"
                  color="primary"
                  aria-label={onOpenRelatedEntity ? `Open ${label}` : `Open ${label} in new tab`}
                  title={onOpenRelatedEntity ? `Open ${label}` : `Open ${label} in new tab`}
                  onClick={handleOpenRelated}
                  sx={{ mt: 1.25, flexShrink: 0 }}
                >
                  {onOpenRelatedEntity ? <LaunchIcon fontSize="small" /> : <OpenInNewIcon fontSize="small" />}
                </IconButton>
              )}
            </Box>
          );
        }}
      />

      {isWithAddNewFeature && addPermission && addNew && (
        <CreateDialog isOpen={addNew} closePopup={() => setAddNew(false)} onItemCreate={onItemCreate} />
      )}
    </div>
  );
};

SelectWithAddSearch.propTypes = {
  createDialog: PropTypes.func,
  control: PropTypes.shape({}).isRequired,
  setValue: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  error: PropTypes.string,
  url: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  moduleName: PropTypes.string,
  disabled: PropTypes.bool,
  selectCompany: PropTypes.func,
  relatedEntityLink: PropTypes.shape({
    kind: PropTypes.oneOf(["contact", "company", "document", "product"]).isRequired
  }),
  onOpenRelatedEntity: PropTypes.func
};

SelectWithAddSearch.defaultProps = {
  selectCompany: undefined,
  createDialog: undefined,
  error: undefined,
  disabled: false,
  relatedEntityLink: undefined,
  onOpenRelatedEntity: undefined
};

export default SelectWithAddSearch;
